import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { ToastService, ToastType } from '../../../core/services/toast';

/** Teintes de pastille par nature de snackbar (§4.3). */
const TONES: Record<ToastType, { bg: string; fg: string }> = {
  success: { bg: 'rgba(43,212,138,0.25)', fg: '#2BD48A' },
  info: { bg: 'rgba(91,108,255,0.3)', fg: '#8F9BFF' },
  warning: { bg: 'rgba(255,177,61,0.25)', fg: '#FFB13D' },
  error: { bg: 'rgba(224,84,78,0.25)', fg: '#FF8A80' },
  neutral: { bg: 'rgba(255,255,255,0.18)', fg: '#ffffff' },
  dispute: { bg: 'rgba(123,92,255,0.3)', fg: '#C9B8FF' },
};

/**
 * Pile de snackbars, centrée en haut de l'écran (§4.3).
 *
 * Le conteneur est en `pointer-events: none` et les pastilles ne sont pas
 * cliquables : une snackbar informe, elle ne réclame pas d'interaction et ne
 * doit jamais intercepter un tap destiné au contenu en dessous.
 *
 * Sortie animée puis retrait du DOM : `snack-out` démarre 400 ms avant
 * l'échéance pour que la pastille ait fini de disparaître au moment où le
 * service la retire.
 */
@Component({
  selector: 'app-toast-container',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="pointer-events-none fixed inset-x-0 z-50 flex flex-col items-center gap-2 px-4"
      style="top: max(52px, calc(env(safe-area-inset-top, 0px) + 8px))"
    >
      @for (toast of toastService.toasts(); track toast.id) {
      <div
        class="inline-flex items-center gap-[9px] rounded-full border border-white/10 py-2 pr-3.5 pl-2 shadow-[0_14px_34px_-12px_rgba(0,0,0,0.7)] backdrop-blur-[16px]"
        style="background: rgba(20,21,36,0.92)"
        [style.animation]="animation(toast.duration)"
        role="alert"
        aria-live="polite"
      >
        <span
          class="grid h-[26px] w-[26px] place-items-center rounded-full"
          [style.background]="tone(toast.type).bg"
        >
          <span class="material-icons-round text-[15px]" [style.color]="tone(toast.type).fg">
            {{ toast.icon }}
          </span>
        </span>
        <span class="text-[12.5px] font-extrabold whitespace-nowrap text-white">
          {{ toast.message }}
        </span>
      </div>
      }
    </div>
  `,
})
export class ToastContainer {
  readonly toastService = inject(ToastService);

  protected tone(type: ToastType): { bg: string; fg: string } {
    return TONES[type] ?? TONES.info;
  }

  protected animation(duration: number): string {
    const exit = Math.max(0, duration - 400) / 1000;
    return `snack-in 0.45s cubic-bezier(0.34,1.56,0.64,1) both, snack-out 0.3s ease ${exit}s forwards`;
  }
}
